import { defineStore } from "pinia";

import type { CustomersState } from "@/Modules/Customers/Models/State";
import type { User } from "@/Modules/Models/Domain/User";
import type { UserDischarge, DeleteAccount } from "./Models/Domain";
import { StateNamespace } from "@/Modules/App/Models/State/StateNamespace";

import fetchCustomerAH from "./ActionHandlers/FetchCustomer";
import requestTranscriptionAH from "./ActionHandlers/RequestTranscription";
import configTranscriptionAH from "./ActionHandlers/ConfigTranscription";
import dischargePatientAH from "./ActionHandlers/Discharge/DischargePatient";
import fetchUserDischargeAH from "./ActionHandlers/Discharge/FetchUserDischarge";
import SendMissingEmailActionHandler from "@/Modules/Therapist/ActionHandlers/SendMissingEmail";
import deleteAccountAH from "./ActionHandlers/DeleteAccount";

import { hasValue } from "@/Utils/Objects";

const customerState: CustomersState = {
  customers: {},
  selected: null,
  isLoading: false,
  notification: null,
  isHasNoSubscription: false,
};

export const useCustomerStore = defineStore(StateNamespace.CUSTOMER, {
  state: () => ({ ...customerState }),
  getters: {
    getCurrentCustomer: (state: CustomersState) => {
      return state.selected;
    },
  },
  actions: {
    loadCustomer(customerId: string) {
      return fetchCustomerAH(customerId);
    },

    selectCustomer(customerId: string) {
      if (hasValue(this.customers[customerId])) {
        this.selected = this.customers[customerId];
      }
    },

    async fetchCustomer(customerId: string): Promise<void> {
      this.isLoading = true;

      if (!hasValue(this.customers[customerId])) {
        await this.loadCustomer(customerId);
      } else {
        this.selected = this.customers[customerId];
      }

      this.isLoading = false;
    },

    async addOrModifyCustomer(user: User) {
      await this.loadCustomer(user.id);
    },

    async sendMissingEmail(uuid: string) {
      const sendMissingEmailHandler = new SendMissingEmailActionHandler(this);

      await sendMissingEmailHandler.handle(uuid);
    },

    dischargeCustomer(reason: string): Promise<true | undefined> {
      return dischargePatientAH(reason);
    },

    getUserDischarge(): Promise<UserDischarge | undefined> {
      return fetchUserDischargeAH();
    },

    requestTranscription(status: boolean): Promise<boolean | undefined> {
      return requestTranscriptionAH(status);
    },

    configTranscription(accept: boolean): Promise<boolean | undefined> {
      return configTranscriptionAH({ status: accept });
    },

    deleteAccount(customerId: string, deleteAccountData: DeleteAccount): Promise<boolean | undefined> {
      return deleteAccountAH(customerId, deleteAccountData);
    },
  },
});

export type CustomerStore = ReturnType<typeof useCustomerStore>;

export default useCustomerStore;
